const ignoredRoutes = new Set([
  '/404/',
  '/404.html',
  '/dev-404-page/',
  '/offline-plugin-app-shell-fallback/',
  '/services/production-offline/exhibition-event-branding/',
  '/services/production-offline/exhibition-event-branding',
  '/services/exhibition-event-branding/',
  '/services/exhibition-event-branding',
]);

export const routeLabel = (path) => {
  const slug = path.replace(/^\//, '').replace(/\/$/, '').split('/').pop() || 'Home';
  return slug
    .replace(/-usa$/, ' USA')
    .replace(/-in$/, ' India')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const getPublicRouteItems = (pages = []) => {
  const paths = [...new Set(pages.map(({ path }) => path))]
    .filter((path) => path && !ignoredRoutes.has(path));

  return paths
    .sort((a, b) => a.localeCompare(b))
    .map((path) => ({
      name: path === '/' ? 'Home' : routeLabel(path),
      desc: path,
      path,
      icon: path.startsWith('/tools/') ? '🧰' : '',
      color: path.startsWith('/tools/') ? '#1565c0' : '#64748b',
    }));
};

const toolCategoryNames = {
  amazon: 'Amazon Tools',
  flipkart: 'Flipkart Tools',
  meesho: 'Meesho Tools',
  walmart: 'Walmart Tools',
  ebay: 'eBay Tools',
  shopify: 'Shopify Tools',
  calculators: 'Calculator Tools',
  'image-optimization': 'Image Optimization',
  'image-editing': 'Image Editing',
  'image-conversion': 'Image Conversion',
  'color-tools': 'Color & Design Tools',
  'text-tools': 'Text Tools',
  utilities: 'Utility Tools',
};

const getToolCategory = (path) => {
  const parts = path.split('/').filter(Boolean);
  if (parts[1] === 'marketplace') {
    const region = parts[2] ? routeLabel(`/${parts[2]}`) : 'Marketplace';
    return `Marketplace Tools · ${region}`;
  }

  return toolCategoryNames[parts[1]] || 'Other Business Tools';
};

export const getCategorizedToolSections = (routes = []) => {
  const grouped = routes.reduce((categories, route) => {
    const category = getToolCategory(route.path);
    if (!categories[category]) categories[category] = [];
    categories[category].push(route);
    return categories;
  }, {});

  return Object.entries(grouped)
    .sort(([first], [second]) => first.localeCompare(second))
    .map(([title, items]) => ({
      title,
      items: items.sort((first, second) => first.name.localeCompare(second.name)),
    }));
};

const canonicalToolSectionOrder = [
  'Typography & Font Engineering',
  'Amazon Seller Tools',
  'Flipkart Seller Tools',
  'Meesho Seller Tools',
  'Fashion Marketplace Tools',
  'Quick Commerce Tools',
  'Marketplace & E-Commerce Tools',
  'Image Optimization',
  'Image Editing',
  'Image Conversion',
  'Color Palette',
  'Color Utilities',
  'Text Utilities',
  'Developer Utilities',
  'Social Media Utilities',
];

const getCanonicalToolSection = (route) => {
  const value = `${route.path} ${route.name}`.toLowerCase();

  if (/(typography|glyph|kerning|bezier|letterform|font|type-scale|typeface)/.test(value)) {
    return 'Typography & Font Engineering';
  }
  if (value.includes('amazon')) return 'Amazon Seller Tools';
  if (value.includes('flipkart')) return 'Flipkart Seller Tools';
  if (value.includes('meesho')) return 'Meesho Seller Tools';
  if (/(myntra|nykaa|ajio|fashion)/.test(value)) return 'Fashion Marketplace Tools';
  if (/(blinkit|zepto|swiggy|quick.?commerce|quick.?commerce)/.test(value)) return 'Quick Commerce Tools';

  if (/(compress|upscal|image.?optimization)/.test(value)) return 'Image Optimization';
  if (/(resize|crop|rotate|image.?editing)/.test(value)) return 'Image Editing';
  if (/(convert|image.?conversion)/.test(value)) return 'Image Conversion';
  if (/(palette|color|contrast|tailwind|image.?picker)/.test(value)) {
    return /(palette|explore|visualizer)/.test(value) ? 'Color Palette' : 'Color Utilities';
  }
  if (/(case.?converter|text.?utility)/.test(value)) return 'Text Utilities';
  if (/(beautif|developer|code.?utility)/.test(value)) return 'Developer Utilities';
  if (/(hashtag|banned.?hashtag|social.?media)/.test(value)) return 'Social Media Utilities';

  return 'Marketplace & E-Commerce Tools';
};

export const getCanonicalToolSections = (routes = []) => {
  const uniqueRoutes = [...new Map(routes.map((route) => [route.path, route])).values()];
  const grouped = uniqueRoutes.reduce((sections, route) => {
    const section = getCanonicalToolSection(route);
    if (!sections[section]) sections[section] = [];
    sections[section].push(route);
    return sections;
  }, {});

  return canonicalToolSectionOrder
    .filter((title) => grouped[title]?.length)
    .map((title) => ({
      title,
      items: grouped[title].sort((first, second) => first.name.localeCompare(second.name)),
    }));
};
